import { EMPTY_ARRAY, SideEnum, useSecuritiesContext, useUnifiedLiquidity, type Security } from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useState, type ComponentProps, type RefObject } from 'react';
import { MultilegMarketSelector } from './MultilegMarketSelector';
import { getMultilegLegSide } from './multilegSide';
import { getUnifiedLiquidityLegSymbols } from './unifiedLiquidityLeg';
import { MULTILEG_UNIFIED_LIQUIDITY_LEG_INDEX as UL_LEG_INDEX, useMultilegLegButtons } from './useMultilegLegButtons';

interface MultilegQuickMarketSelectorPanelProps {
  security: Security;
  legMarketAccounts: string[][];
  onLegMarketAccountsChange: (update: { legIndex: number; value: string[] }) => void;
  unifiedLiquidityMarketAccounts?: string[];
  onUnifiedLiquidityMarketAccountsChange?: (marketAccounts: string[]) => void;
  includeUnifiedLiquidity?: boolean;
  isOpen: boolean;
  onClose: () => void;
  panelAnchorRef: RefObject<HTMLElement>;
  panelBottomSuffix?: ComponentProps<typeof MultilegMarketSelector>['panelBottomSuffix'];
  isMarketSelectorNarrow?: boolean;
  initialLegIndex?: number;
}

/**
 * Reusable quick selector for editing market account selections across regular multileg legs.
 * Unified Liquidity is optional because only Market Data Cards persist a synthetic UL leg today.
 */
export function MultilegQuickMarketSelectorPanel({
  security,
  legMarketAccounts,
  onLegMarketAccountsChange,
  unifiedLiquidityMarketAccounts = EMPTY_ARRAY,
  onUnifiedLiquidityMarketAccountsChange,
  includeUnifiedLiquidity = false,
  isOpen,
  onClose,
  panelAnchorRef,
  panelBottomSuffix,
  isMarketSelectorNarrow,
  initialLegIndex = 0,
}: MultilegQuickMarketSelectorPanelProps) {
  const legDetails = useMemo(() => security.MultilegDetails?.Legs ?? [], [security.MultilegDetails?.Legs]);
  const [activeLegIndex, setActiveLegIndex] = useState(initialLegIndex);
  const unifiedLiquidityResponse = useUnifiedLiquidity(security.Symbol);
  const { securitiesBySymbol } = useSecuritiesContext();

  // If initialLegIndex changes or isOpen changes, reset the active leg index
  useEffect(() => {
    if (isOpen) {
      setActiveLegIndex(initialLegIndex);
    }
  }, [initialLegIndex, isOpen]);

  const { representativeSymbol: unifiedLiquidityRepresentativeSymbol, displaySymbols: unifiedLiquidityDisplaySymbols } =
    getUnifiedLiquidityLegSymbols(unifiedLiquidityResponse, securitiesBySymbol);

  const legButtons = useMultilegLegButtons({
    legDetails,
    resolvedLegMarkets: legMarketAccounts,
    unifiedLiquidityMarkets: unifiedLiquidityMarketAccounts,
    unifiedLiquidityDisplaySymbols,
    includeUnifiedLiquidity: includeUnifiedLiquidity && unifiedLiquidityRepresentativeSymbol != null,
    showAllMarketsToggle: true,
  });

  const activeLegMarketSelectorProps = getActiveLegMarketSelectorProps({
    activeLegIndex,
    legDetails,
    resolvedLegMarkets: legMarketAccounts,
    securitiesBySymbol,
    unifiedLiquidityMarkets: unifiedLiquidityMarketAccounts,
    unifiedLiquidityRepresentativeSymbol,
  });

  const handleMarketSelectorChange = useCallback(
    (value: string[]) => {
      if (activeLegIndex === UL_LEG_INDEX) {
        onUnifiedLiquidityMarketAccountsChange?.(value);
        return;
      }

      onLegMarketAccountsChange({ legIndex: activeLegIndex, value });
    },
    [activeLegIndex, onLegMarketAccountsChange, onUnifiedLiquidityMarketAccountsChange]
  );

  // We only return the market selector component if we are open. This helps with performance. Our component here can be rendered many at once in the price grid, for example.
  if (!isOpen) {
    return null;
  }

  return (
    <MultilegMarketSelector
      legIndex={activeLegIndex === UL_LEG_INDEX ? undefined : activeLegIndex}
      isOpen={isOpen}
      close={onClose}
      security={activeLegMarketSelectorProps.security}
      value={activeLegMarketSelectorProps.value}
      showAllMarketsToggle={activeLegIndex !== UL_LEG_INDEX}
      showSpinner={false}
      onChange={handleMarketSelectorChange}
      marketSelectorAnchorRef={panelAnchorRef}
      dropdownAnchorRef={panelAnchorRef}
      side={activeLegMarketSelectorProps.side}
      subAccountAllocations={-1}
      legButtons={legButtons}
      onLegClick={setActiveLegIndex}
      panelBottomSuffix={panelBottomSuffix}
      isMarketSelectorNarrow={isMarketSelectorNarrow}
    />
  );
}

type MultilegLegDetails = NonNullable<NonNullable<Security['MultilegDetails']>['Legs']>;

function getActiveLegMarketSelectorProps({
  activeLegIndex,
  legDetails,
  resolvedLegMarkets,
  securitiesBySymbol,
  unifiedLiquidityMarkets,
  unifiedLiquidityRepresentativeSymbol,
}: {
  activeLegIndex: number;
  legDetails: MultilegLegDetails;
  resolvedLegMarkets: string[][];
  securitiesBySymbol: Map<string, Security>;
  unifiedLiquidityMarkets: string[];
  unifiedLiquidityRepresentativeSymbol: string | undefined;
}): {
  security: Security | undefined;
  value: string[];
  side: SideEnum;
} {
  if (activeLegIndex === UL_LEG_INDEX) {
    return {
      security: unifiedLiquidityRepresentativeSymbol
        ? securitiesBySymbol.get(unifiedLiquidityRepresentativeSymbol)
        : undefined,
      value: unifiedLiquidityMarkets,
      side: SideEnum.Buy,
    };
  }

  const activeLegDetails = legDetails.at(activeLegIndex);

  return {
    security: activeLegDetails?.Symbol ? securitiesBySymbol.get(activeLegDetails.Symbol) : undefined,
    value: resolvedLegMarkets.at(activeLegIndex) ?? [],
    side: getMultilegLegSide(SideEnum.Buy, activeLegDetails?.Direction),
  };
}
